import { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useTeamUserNamesById } from "@redotech/redo-merchant-app-common/hooks/use-team-users";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import {
  RedoBadge,
  type RedoBadgeColor,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoPhoneNumberInput } from "@redotech/redo-web/arbiter-components/input/redo-phone-number-input";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { UserImage, UserImageSize } from "@redotech/redo-web/user-image";
import { memo, useContext, useMemo, useState } from "react";
import { E164_RE, normalizeMaskedPhone } from "./phone-normalize";
import { useGetVoiceCallAvailability } from "./use-voice-actions";
import { VoiceContextV2 } from "./voice-context-v2";

type Tab = "agents" | "phone";

/**
 * Inline transfer panel — same agents/phone-number UX as the old
 * `TransferModalV2`, but rendered into the active-call view instead
 * of opening a modal. Mounted only while the operator has the
 * Transfer toggle on, side-by-side with the keypad's swap-out
 * behavior.
 *
 * `onTransferred` is called once the transfer has been kicked off;
 * the parent typically uses it to flip the panel back to the default
 * controls so the operator sees the new "Ringing X..." placeholder
 * appear in the participants list.
 */
export const TransferPanelV2 = memo(function TransferPanelV2({
  onTransferred,
}: {
  readonly onTransferred: () => void;
}) {
  const [tab, setTab] = useState<Tab>("agents");
  return (
    <Flex dir="column" gap="md" w="full">
      <Flex gap="xs" w="full">
        <Flex basis="0" grow={1}>
          <RedoButton
            hierarchy={tab === "agents" ? "primary" : "tertiary"}
            onClick={() => setTab("agents")}
            size="xs"
            text="Agents"
          />
        </Flex>
        <Flex basis="0" grow={1}>
          <RedoButton
            hierarchy={tab === "phone" ? "primary" : "tertiary"}
            onClick={() => setTab("phone")}
            size="xs"
            text="Phone number"
          />
        </Flex>
      </Flex>
      {/* Fixed-height container so the panel doesn't reflow when the
          agent list is short or empty (which would shrink the widget
          uncomfortably) and scrolls when the list is long. */}
      <Flex dir="column" style={{ height: 240, overflowY: "auto" }} w="full">
        {tab === "agents" ? (
          <AgentsTab onTransferred={onTransferred} />
        ) : (
          <PhoneTab onTransferred={onTransferred} />
        )}
      </Flex>
    </Flex>
  );
});

// Wire status enum is lowercase: `available | in-call | unavailable`.
const STATUS_BADGE: Record<string, { text: string; color: RedoBadgeColor }> = {
  available: { text: "Available", color: "success" },
  "in-call": { text: "On call", color: "warning" },
  unavailable: { text: "Offline", color: "gray" },
};

// Sort order so available agents bubble to the top of the list — the
// operator's most likely transfer target.
const STATUS_RANK: Record<string, number> = {
  available: 0,
  "in-call": 1,
  unavailable: 2,
};

const AgentsTab = memo(function AgentsTab({
  onTransferred,
}: {
  readonly onTransferred: () => void;
}) {
  const ctx = useContext(VoiceContextV2);
  if (ctx == null) {
    throw new Error("VoiceContextV2 missing");
  }
  const user = useRequiredContext(UserContext);
  const userNamesById = useTeamUserNamesById();
  const getAvailability = useGetVoiceCallAvailability();
  const availabilityLoad = useLoad(async () => getAvailability(), []);

  // Whoever the operator is acting as (impersonation in the dev
  // harness, or just their own account in prod) should not appear as
  // a transfer target — you can't transfer a call to yourself.
  const ownMongoUserId = useMemo<MongoUserId | null>(() => {
    if (ctx.targetMongoUserId !== undefined) {
      return ctx.targetMongoUserId;
    }
    if (!user?._id) {
      return null;
    }
    try {
      return MongoUserId.fromString(user._id);
    } catch {
      return null;
    }
  }, [ctx.targetMongoUserId, user]);

  // Users already on the conference (agents + forwarded-phone legs)
  // shouldn't show up either — transferring to someone who's already
  // there would just no-op or double-ring them.
  const onCallMongoUserIds = useMemo(() => {
    const ids = new Set<MongoUserId>();
    for (const p of ctx.participants) {
      if (p.kind === "agent" || p.kind === "forwarded_phone") {
        ids.add(p.mongoUserId);
      }
    }
    return ids;
  }, [ctx.participants]);

  const rows = useMemo(() => {
    const all = availabilityLoad.value ?? [];
    const filtered = all.filter((row) => {
      if (ownMongoUserId != null && row.mongoUserId === ownMongoUserId) {
        return false;
      }
      if (onCallMongoUserIds.has(row.mongoUserId)) {
        return false;
      }
      return true;
    });
    filtered.sort(
      (a, b) => (STATUS_RANK[a.status] ?? 99) - (STATUS_RANK[b.status] ?? 99),
    );
    return filtered;
  }, [availabilityLoad.value, ownMongoUserId, onCallMongoUserIds]);

  const handleSelect = (mongoUserId: MongoUserId, name: string) => {
    ctx.addAgentToCall(mongoUserId, name);
    onTransferred();
  };

  if (availabilityLoad.pending) {
    return <RedoSpinner size="xs" />;
  }

  if (rows.length === 0) {
    return (
      <Text fontSize="xs" textColor="secondary">
        No other agents on this team.
      </Text>
    );
  }

  return (
    <Flex dir="column" gap="xs" w="full">
      {rows.map((row) => {
        const name =
          userNamesById.get(String(row.mongoUserId)) ?? "Unknown user";
        const status = STATUS_BADGE[row.status] ?? STATUS_BADGE.unavailable!;
        return (
          <Flex
            align="center"
            bgColor="primary"
            borderColor="secondary"
            borderStyle="solid"
            borderWidth="1px"
            cursor="pointer"
            gap="sm"
            key={String(row.mongoUserId)}
            onClick={() => handleSelect(row.mongoUserId, name)}
            p="sm"
            radius="sm"
          >
            <UserImage alt={name} imageUrl={null} size={UserImageSize.X_TINY} />
            <Flex dir="column" gap="none" grow={1}>
              <Text fontSize="xxs" textColor="primary">
                {name}
              </Text>
            </Flex>
            <RedoBadge color={status.color} size="xs" text={status.text} />
          </Flex>
        );
      })}
    </Flex>
  );
});

const PhoneTab = memo(function PhoneTab({
  onTransferred,
}: {
  readonly onTransferred: () => void;
}) {
  const ctx = useContext(VoiceContextV2);
  if (ctx == null) {
    throw new Error("VoiceContextV2 missing");
  }
  const [phone, setPhone] = useState("");

  // The masked input emits values like "+1 (555) 555-0123"; Telnyx and
  // the backend expect E.164 ("+15555550123"). Normalize before send so
  // the dial actually goes through, and gate the Call button on the
  // result being a valid E.164 number.
  const normalized = normalizeMaskedPhone(phone);
  const isValid = E164_RE.test(normalized);

  const submit = () => {
    if (!isValid) {
      return;
    }
    ctx.addPhoneNumberToCall(normalized);
    onTransferred();
  };

  return (
    <Flex dir="column" gap="sm">
      <RedoPhoneNumberInput
        errorText={
          phone.trim() !== "" && !isValid
            ? "Use international format, e.g. +15555550123"
            : undefined
        }
        setValue={setPhone}
        state={phone.trim() === "" || isValid ? "default" : "error"}
        value={phone}
      />
      <RedoButton
        disabled={!isValid}
        hierarchy="primary"
        onClick={submit}
        size="sm"
        text="Call"
      />
    </Flex>
  );
});
